import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTitle } from '../context/TitleContext';
import { Send, User, Calendar, FileText, CheckCircle, Clock, Bell, Download } from 'lucide-react';
import { exportActivityToFile } from '../services/activityBackup';

// Interface for sent request activity
interface SentRequest {
  id: string;
  templateName: string;
  templateId?: string; // Added to enable navigation to template
  recipientEmail: string;
  sentAt: string;
  status: 'sent' | 'viewed' | 'completed';
  documentCount: number;
  actionType?: 'sent' | 'reminder'; // Track if it's initial send or reminder
  sentBy?: string; // User who initiated the action
}

const RecentActivity = () => {
  const { t } = useTranslation();
  const { setTitle } = useTitle();
  const navigate = useNavigate();
  const [activities, setActivities] = useState<SentRequest[]>([]);

  useEffect(() => {
    setTitle(t('recentActivity'));
    document.title = t('recentActivity');
  }, [setTitle, t]);

  useEffect(() => {
    // Load activities from localStorage
    const storedActivities = localStorage.getItem('sentRequests');
    if (storedActivities) {
      setActivities(JSON.parse(storedActivities));
    }
  }, []);

  const getActionIcon = (actionType?: string) => {
    if (actionType === 'reminder') {
      return <Bell className="h-4 w-4 text-orange-600 dark:text-orange-400 mr-1" />;
    }
    return null;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />;
      case 'viewed':
        return <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />;
      default:
        return <Send className="h-5 w-5 text-gray-600 dark:text-gray-400" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return t('statusCompleted');
      case 'viewed':
        return t('statusViewed');
      default:
        return t('statusSent');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300';
      case 'viewed':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) {
      return `${diffMins} ${t('minutesAgo')}`;
    } else if (diffHours < 24) {
      return `${diffHours} ${t('hoursAgo')}`;
    } else if (diffDays < 7) {
      return `${diffDays} ${t('daysAgo')}`;
    } else {
      return date.toLocaleDateString();
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t('recentActivity')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {t('recentActivityDescription')}
          </p>
        </div>
        
        {activities.length > 0 && (
          <button
            onClick={exportActivityToFile}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 transition-colors"
            title={t('exportBackup')}
          >
            <Download className="h-4 w-4 mr-2" />
            {t('exportBackup')}
          </button>
        )}
      </div>

      {activities.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center border border-gray-200 dark:border-gray-700">
          <Send className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t('noActivityYet')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {t('noActivityYetDescription')}
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('template')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('recipient')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('sentBy')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('documents')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('sentDate')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('status')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {activities.map((activity) => (
                  <tr key={activity.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getActionIcon(activity.actionType)}
                        <FileText className="h-5 w-5 text-gray-400 mr-3" />
                        {activity.templateId ? (
                          <button
                            onClick={() => navigate(`/my-templates`)}
                            className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            {activity.templateName}
                          </button>
                        ) : (
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {activity.templateName}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="h-4 w-4 text-gray-400 mr-2" />
                        <div className="text-sm text-gray-900 dark:text-white">
                          {activity.recipientEmail}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="h-4 w-4 text-blue-400 mr-2" />
                        <div className="text-sm text-gray-900 dark:text-white">
                          {activity.sentBy || t('unknown')}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {activity.documentCount} {t('documents')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                        <Calendar className="h-4 w-4 mr-2" />
                        {formatDate(activity.sentAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(activity.status)}`}>
                        {getStatusIcon(activity.status)}
                        <span className="ml-1">{getStatusText(activity.status)}</span>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecentActivity;
